import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { UnauthedPage } from 'components/unauthed-page';
export type ForgotPasswordProps = {
  email: string;
  onChange: (field: string, val: string) => void;
  onSubmit: () => void;
  error: string;
  disableSubmit: boolean;
  submitted: boolean;
};

export const ForgotPassword = observer((props: ForgotPasswordProps) => {
  const { t } = useScopedTranslate(locale);
  const { email, onChange, onSubmit, error, disableSubmit, submitted } = props;
  const submitLabel = disableSubmit ? t('submitting') : t('submit');

  if (submitted) {
    return (
      <UnauthedPage>
        <Stack space={6}>
          <Stack align="center">
            <Text font="heading1" text={t('checkEmail')} />
            <Text font="regular14" color="textSecondary" center>
              {t('registered')} <BlueText>{props.email}</BlueText>
            </Text>
          </Stack>
          <Text font="regular14" color="textSecondary" center text={t('unregistered')} />
          <ActionText.Link
            to="/signin"
            children={t('backToSignin')}
            font="regular14"
            color="textHelper"
            center
          />
        </Stack>
      </UnauthedPage>
    );
  }

  return (
    <UnauthedPage>
      <Stack space={6}>
        <Stack align="center" space={1}>
          <Text font="heading1" text={t('forgotYourPassword')} />
          <Text font="regular14" color="textSecondary" center text={t('enterEmail')} />
        </Stack>
        <TextField
          label={t('core.email')}
          autoFocus
          error={error}
          value={email}
          onChange={(val) => onChange('email', val)}
          isDisabled={disableSubmit}
        />
        <Button
          isDisabled={disableSubmit}
          onClick={onSubmit}
          text={submitLabel}
          variant="primary-blue"
          style={{ width: '100%' }}
        />
        <ActionText.Link
          to="/signin"
          children={t('backToSignin')}
          font="regular14"
          color="textHelper"
          center
        />
      </Stack>
    </UnauthedPage>
  );
});

const BlueText = styled.span({
  color: theme.color.elementBlue100,
});

const locale = {
  en: {
    submitting: 'Resetting...',
    submit: 'Reset password',
    forgotYourPassword: 'Forgot your password?',
    enterEmail:
      "Enter your email address and we'll send you instructions for resetting your password",
    backToSignin: 'Back to signin page',
    registered:
      'If you have a registered account, you should receive an email with instructions on how to reset your password shortly at',
    unregistered:
      'If you do not receive an email within the next 5 minutes, please check your spam folder, contact support or an administrator at your company',
    checkEmail: 'Check your email',
  },
  is: {
    submitting: 'Endurstilli...',
    submit: 'Endurstilla lykilorð',
    forgotYourPassword: 'Gleymdirðu lykilorðinu þínu?',
    enterEmail:
      'Skráðu inn netfangið þitt og við sendum á þig leiðbeiningar til að endurstilla lykilorðið þitt',
    backToSignin: 'Aftur á innskráningarsíðu',
    registered:
      'Ef þú hefur skráðan aðgang ættirðu að fá slóð til að endursetja lykilorðið þitt innan skamms á:',
    unregistered:
      'Ef ekkert hefur borist á næstu 5 mínútum, vinsamlegast athugaðu hvort hann hafi óvart lent í ruslpóstinum, hafðu samband við okkur eða stjórnanda hjá þínu fyrirtæki.',
    checkEmail: 'Vinsamlegast athugaðu póstinn þinn',
  },
};
